import { useState, useEffect, useContext, useRef } from 'react';
import { AuthContext, API } from '@/App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { Camera, CheckCircle, MapPin, LogOut, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Webcam from 'react-webcam';
import { compareFaces, loadFaceModels } from '@/utils/faceMatching';
import { ResetOwnPassword } from '@/components/PasswordReset';

export default function EmployeeDashboard() {
  const { user, token, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const webcamRef = useRef(null);
  const [hasActiveCheckIn, setHasActiveCheckIn] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [todayAttendance, setTodayAttendance] = useState(null);
  const [salary, setSalary] = useState(null);
  const [modelsLoaded, setModelsLoaded] = useState(false);

  useEffect(() => {
    // Load face recognition models
    loadFaceModels().then(loaded => {
      setModelsLoaded(loaded);
      if (loaded) {
        console.log('✅ Face recognition ready');
      }
    });
    
    fetchEmployeeData();
    const interval = setInterval(fetchEmployeeData, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchEmployeeData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [attendanceRes, salaryRes] = await Promise.all([
        axios.get(`${API}/attendance/my-attendance`, { headers }),
        axios.get(`${API}/payroll/my-salary`, { headers })
      ]);

      const today = new Date().toISOString().split('T')[0];
      const todayRecord = attendanceRes.data.find(a => a.date === today);
      
      setTodayAttendance(todayRecord || null);
      setHasActiveCheckIn(todayRecord && !todayRecord.check_out_time);
      setSalary(salaryRes.data);
    } catch (error) {
      console.error('Failed to fetch employee data:', error);
    }
  };

  const handleAttendanceClick = () => {
    setShowCamera(true);
  };

  const handleCapture = async () => {
    setIsProcessing(true);

    try {
      // Capture image first
      const liveImageSrc = webcamRef.current.getScreenshot();
      
      // Get registered face image from user data
      const registeredImage = user.face_image;
      
      if (!registeredImage) {
        toast.error('No registered face image found. Please contact admin to re-register.');
        setIsProcessing(false);
        return;
      }
      
      // Check if models are loaded
      if (!modelsLoaded) {
        toast.error('Face recognition models not loaded. Please refresh the page.');
        setIsProcessing(false);
        return;
      }
      
      // Show matching in progress
      const matchingToast = toast.loading('🔍 Analyzing your face...');
      
      // Compare faces using face-api.js
      const matchResult = await compareFaces(registeredImage, liveImageSrc);
      
      toast.dismiss(matchingToast);
      
      console.log('Face Match Result:', matchResult);
      
      if (!matchResult.success) {
        toast.error(
          <div>
            <p className="font-semibold">{matchResult.message}</p>
            <p className="text-xs mt-1">Confidence: {matchResult.confidencePercent}%</p>
          </div>, 
          { duration: 7000 }
        );
        setIsProcessing(false);
        return;
      }
      
      // Face matched! Now check location
      toast.info('✅ Face verified! Checking location...');
      
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            const endpoint = hasActiveCheckIn ? '/attendance/check-out' : '/attendance/check-in';
            
            const response = await axios.post(
              `${API}${endpoint}`,
              {
                face_image: liveImageSrc,
                latitude: position.coords.latitude,
                longitude: position.coords.longitude,
                ground_id: null,
                face_confidence: matchResult.confidence
              },
              { headers: { Authorization: `Bearer ${token}` } }
            );

            // Show success with BOTH match percentages
            const data = response.data;
            const statusIcon = data.status === 'verified' ? '✅' : '⚠️';
            
            toast.success(
              <div>
                <p className="font-semibold">{statusIcon} {data.message}</p>
                <div className="mt-2 space-y-1">
                  <p className="text-sm">📸 Face Match: {data.face_match_percentage}%</p>
                  <p className="text-sm">📍 Location Match: {data.location_match_percentage}%</p>
                  <p className="text-xs font-semibold mt-2">{data.verification_message}</p>
                </div>
              </div>,
              { duration: 6000 }
            );
            setShowCamera(false);
            fetchEmployeeData();
          } catch (error) {
            // Show specific error message from backend
            const errorMsg = error.response?.data?.detail || 'Failed to record attendance';
            toast.error(errorMsg, { duration: 6000 });
          } finally {
            setIsProcessing(false);
          }
        },
        (error) => {
          toast.error('📍 Please enable location access in your browser settings', { duration: 5000 });
          setIsProcessing(false);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        }
      );
    } catch (error) {
      toast.error('Face matching failed. Please try again with better lighting.');
      setIsProcessing(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-emerald-50 to-cyan-50" data-testid="employee-dashboard">
      {/* Simple Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div>
              <h1 className="text-xl font-bold text-gray-800" style={{fontFamily: 'Space Grotesk, sans-serif'}}>TurfSense</h1>
              <p className="text-sm text-gray-600 flex items-center gap-2" style={{fontFamily: 'Inter, sans-serif'}}>
                Hello, {user.full_name}
                {modelsLoaded && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Face Recognition Active</span>}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <ResetOwnPassword token={token} />
            <Button variant="outline" size="sm" onClick={handleLogout} data-testid="logout-btn">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        {/* Today's Status Card */}
        {todayAttendance && (
          <Card className="p-6 bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <h3 className="text-lg font-semibold mb-3 text-blue-800" style={{fontFamily: 'Space Grotesk, sans-serif'}}>Today's Status</h3>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-gray-700">Check-in:</span>
                <span className="font-semibold text-blue-600">{new Date(todayAttendance.check_in_time).toLocaleTimeString()}</span>
              </div>
              {todayAttendance.check_out_time && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-700">Check-out:</span>
                  <span className="font-semibold text-blue-600">{new Date(todayAttendance.check_out_time).toLocaleTimeString()}</span>
                </div>
              )}
              <div className="flex justify-between items-center">
                <span className="text-gray-700">Status:</span>
                <span className={`font-semibold ${todayAttendance.check_out_time ? 'text-green-600' : 'text-orange-600'}`}>
                  {todayAttendance.check_out_time ? 'Completed' : 'Active'}
                </span>
              </div>
            </div>
          </Card>
        )}

        {/* Main Attendance Button */}
        {!showCamera ? (
          <Card className="p-12 bg-gradient-to-br from-emerald-500 to-teal-600 text-white text-center shadow-2xl">
            <div className="space-y-6">
              <div className="flex justify-center">
                <div className="h-24 w-24 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                  {hasActiveCheckIn ? (
                    <CheckCircle className="h-14 w-14 text-white" />
                  ) : (
                    <Camera className="h-14 w-14 text-white" />
                  )}
                </div>
              </div>
              
              <div>
                <h2 className="text-3xl font-bold mb-2" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
                  {hasActiveCheckIn ? 'Ready to Check Out?' : 'Start Your Day'}
                </h2>
                <p className="text-lg text-white/90" style={{fontFamily: 'Inter, sans-serif'}}>
                  {hasActiveCheckIn ? 'Click below to complete your attendance' : 'Click below to mark your attendance'}
                </p>
              </div>

              <Button
                onClick={handleAttendanceClick}
                size="lg"
                className="w-full h-20 text-2xl font-bold bg-white text-emerald-600 hover:bg-emerald-50 shadow-xl"
                data-testid="attendance-btn"
                style={{fontFamily: 'Space Grotesk, sans-serif'}}
              >
                {hasActiveCheckIn ? (
                  <>
                    <CheckCircle className="h-8 w-8 mr-3" />
                    CHECK OUT
                  </>
                ) : (
                  <>
                    <Camera className="h-8 w-8 mr-3" />
                    CHECK IN
                  </>
                )}
              </Button>

              <p className="text-sm text-white/80 mt-4">
                📍 System will capture your photo and location automatically
              </p>
            </div>
          </Card>
        ) : (
          <Card className="p-6 bg-white shadow-2xl">
            <h3 className="text-xl font-semibold mb-4 text-center" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
              {hasActiveCheckIn ? 'Take Photo to Check Out' : 'Take Photo to Check In'}
            </h3>
            
            <div className="space-y-4">
              <div className="relative">
                <Webcam
                  ref={webcamRef}
                  screenshotFormat="image/jpeg"
                  className="w-full rounded-lg shadow-lg"
                  videoConstraints={{
                    facingMode: "user"
                  }}
                />
                <div className="absolute top-4 left-4 bg-red-500 text-white px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-2">
                  <div className="h-2 w-2 bg-white rounded-full animate-pulse"></div>
                  LIVE
                </div>
              </div>

              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg border border-blue-200">
                <p className="text-sm text-gray-700 text-center flex items-center justify-center gap-2">
                  {modelsLoaded ? (
                    <>
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span>Face Recognition Active - Match threshold: 75%</span>
                    </>
                  ) : (
                    <>
                      <AlertCircle className="h-4 w-4 text-orange-600" />
                      <span>Loading face recognition models...</span>
                    </>
                  )}
                </p>
              </div>

              <div className="bg-gradient-to-r from-yellow-50 to-orange-50 p-4 rounded-lg border border-yellow-200">
                <p className="text-sm text-gray-700 text-center">
                  ⚠️ Make sure your face is clearly visible and you are within 50km of the ground location
                </p>
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={handleCapture}
                  disabled={isProcessing}
                  className="flex-1 h-14 text-lg font-semibold bg-emerald-600 hover:bg-emerald-700"
                  data-testid="capture-btn"
                >
                  {isProcessing ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white mr-2"></div>
                      Matching Face & Validating...
                    </>
                  ) : (
                    <>
                      <Camera className="h-5 w-5 mr-2" />
                      Capture & Submit
                    </>
                  )}
                </Button>
                <Button
                  onClick={() => setShowCamera(false)}
                  variant="outline"
                  className="h-14 px-8"
                  disabled={isProcessing}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Salary Info Card */}
        {salary && (
          <Card className="p-6 bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200">
            <h3 className="text-lg font-semibold mb-4 text-purple-800" style={{fontFamily: 'Space Grotesk, sans-serif'}}>Your Salary Info</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Position</p>
                <p className="text-lg font-semibold text-gray-800">{salary.position}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Base Salary</p>
                <p className="text-lg font-semibold text-gray-800">₹{salary.base_salary.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Days Present</p>
                <p className="text-lg font-semibold text-gray-800">{salary.days_present} / {salary.working_days}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Current Month</p>
                <p className="text-2xl font-bold text-purple-600">₹{salary.calculated_salary.toLocaleString()}</p>
              </div>
            </div>
            {salary.allowances > 0 && (
              <div className="mt-3 pt-3 border-t border-purple-200">
                <p className="text-sm text-green-600">+ Allowances: ₹{salary.allowances.toLocaleString()}</p>
              </div>
            )}
            {salary.deductions > 0 && (
              <div className="mt-1">
                <p className="text-sm text-red-600">- Deductions: ₹{salary.deductions.toLocaleString()}</p>
              </div>
            )}
          </Card>
        )}

        {/* Simple Instructions */}
        <Card className="p-6 bg-gradient-to-br from-gray-50 to-gray-100">
          <h3 className="text-lg font-semibold mb-3" style={{fontFamily: 'Space Grotesk, sans-serif'}}>How to Use</h3>
          <div className="space-y-2 text-sm text-gray-700">
            <p>✅ <strong>Step 1:</strong> Click the big button above</p>
            <p>📸 <strong>Step 2:</strong> Allow camera and location access</p>
            <p>✅ <strong>Step 3:</strong> Take your photo</p>
            <p>✨ <strong>Done!</strong> Your attendance is marked automatically</p>
          </div>
        </Card>
      </div>
    </div>
  );
}